"use client";

import Link from "next/link";
import Image from "next/image";
import { useGetFeaturedParts, useGetCategoryList } from "@/lib/api/hooks";
import { useI18n, getLocalizedField } from "@/i18n";

const getConditionName = (condition, t) => {
  const names = {
    new: t("parts.condition.new"),
    used: t("parts.condition.used"),
    refurbished: t("parts.condition.refurbished"),
  };
  return names[condition] ?? "";
};

const getPartTypeName = (partType, t) => {
  const names = {
    original: t("parts.partType.original"),
    aftermarket: t("parts.partType.aftermarket"),
    salvage: t("parts.partType.salvage"),
  };
  return names[partType] ?? "";
};

const formatPrice = (price) => {
    if (price == null || isNaN(price)) return "-";
    const millions = (price / 1000000).toFixed(0);
    return `GNF ${millions}M`;
};

export default function PartsPage() {
    const { t, lang } = useI18n();
    const { data: featuredData, isLoading } = useGetFeaturedParts(40);
    const featuredParts = featuredData?.data ?? featuredData ?? [];
    const { data: categoryData, isLoading: categoryLoading } = useGetCategoryList('part');
    const rawCategories = categoryData?.data ?? categoryData ?? [];
    const categories = rawCategories.map(c => {
        let iconSize = 35;
        
        if (c.id === 12) {  // 汽车配件
            iconSize = 25;
        }
       
        if (c.id === 22) {  // 滤清油液
            iconSize =45;
        }

        return { ...c, width: iconSize, height: iconSize };
    });

    // Single shimmer rule, scoped to this page's skeleton
    const skelCss = (
        <style>{`
            @keyframes __skel_parts__ {
                0%   { background-position: -600px 0; }
                100% { background-position:  600px 0; }
            }
            .ps-block {
                background: linear-gradient(90deg, #eef1ee 0px, #f7f9f7 300px, #eef1ee 600px);
                background-size: 1200px 100%;
                animation: __skel_parts__ 1.4s linear infinite;
                border-radius: 6px;
            }
        `}</style>
    );
    const pb = (w, h, extra = {}) => (
        <div className="ps-block" style={{ width: w, height: h, ...extra }} />
    );

    return (<>
      {skelCss}
      <div className="dealers-page">
      <div className="page-header" style={{
        background: "linear-gradient(135deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.55) 100%), url('https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=1600&q=80') center/cover no-repeat",
        borderBottom: "1px solid rgba(255,255,255,0.1)",
        padding: "40px 0 32px",
        position: "relative",
      }}>
        <div className="container">
          <div className="breadcrumb">
            <Link href="/">{t("common.home")}</Link>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6"/></svg>
            <span>{t("nav.parts")}</span>
          </div>
          <h1 className="page-title" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.5)" }}>{t("parts.title")}</h1>
          <p className="page-subtitle" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}>{t("parts.subtitle")}</p>
        </div>
      </div>
      <div className="container" style={{ paddingTop: 0, paddingBottom: "var(--space-8)" }}>

      <div className="section" style={{ padding: "64px 0 0" }}>
        <div className="section-header">
          <div>
            <div className="section-title-eyebrow">{t("parts.hotCategoriesEyebrow")}</div>
            <h2 className="section-title">{t("parts.hotCategories")}</h2>
          </div>
        </div>
        <div className="grid grid-cols-6">
          {categoryLoading ? Array.from({ length: 12 }).map((_, i) => (
            <div key={`cat-skeleton-${i}`} className="card" style={{ textAlign: "center", padding: "var(--space-5) var(--space-3)", minHeight: 120 }}>
              <div style={{ marginBottom: "var(--space-2)", display: "flex", justifyContent: "center" }}>
                {pb(32, 32, { borderRadius: 6 })}
              </div>
              {pb(90, 16, { margin: "0 auto 8px" })}
              {pb(60, 12, { margin: "0 auto" })}
            </div>
          )) : categories.map((c, i) => (<Link key={c.id} href={`/parts/list?categoryId=${c.id}`} className="card" style={{
                textAlign: "center",
                padding: "var(--space-5) var(--space-3)",
                textDecoration: "none",
                color: "inherit",
                display: "block",
            }}>
              <div style={{ marginBottom: "var(--space-2)", display: "flex", justifyContent: "center" }}>
                <Image src={c.icon} alt={getLocalizedField(c, "categoryName", lang)} width={c.width} height={c.height} style={{ objectFit: "contain" }}/>
              </div>
              <div className="text-lg">{getLocalizedField(c, "categoryName", lang)}</div>
              <div className="text-sm text-muted" style={{ color: "var(--color-text-muted)" }}>{c.count} {t("parts.countItem")}</div>
            </Link>))}
        </div>
      </div>
      <div className="section">
        <div className="section-header">
          <div>
            <div className="section-title-eyebrow">{t("parts.featuredEyebrow")}</div>
            <h2 className="section-title">{t("parts.featured")}</h2>
          </div>
          <Link href="/parts/list" className="section-link">{t("parts.viewAll")} →</Link>
        </div>
        <div className="grid grid-cols-4">
          {isLoading ? Array.from({ length: 8 }).map((_, i) => (
            <div key={`skeleton-${i}`} className="part-card" style={{ overflow: "hidden" }}>
              <div className="part-card-image" style={{ padding: 12, boxSizing: "border-box" }}>
                <div style={{
                  position: "absolute", inset: 0, margin: 12,
                  background: "transparent",
                }} />
                {pb("100%", "100%", { position: "absolute", inset: 0, borderRadius: 10 })}
              </div>
              <div style={{ padding: "0 20px 20px 20px" }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
                  {pb(46, 22)}
                  {pb(80, 12, { marginLeft: "auto" })}
                </div>
                {pb("90%", 16, { marginBottom: 8 })}
                {pb("70%", 12, { marginBottom: 12 })}
                {pb(90, 20, { marginBottom: 4 })}
              </div>
            </div>
          )) : featuredParts.map((p, i) => (<Link key={`featuredParts${p.id}`} href={`/parts/${p.id}`} className="part-card" style={{ textDecoration: "none", color: "inherit", display: "block" }}>
              <div className="part-card-image">
                <Image src={p.coverUrl} alt={p.title} layout="fill" objectFit="cover"/>
                <div className="car-card-badges">
                    {p.isFeatured ? (
                        <span className="tag tag-success">{t("parts.promoted")}</span>
                    ) : null}
                </div>
              </div>
              <div style={{ padding: "0 20px 20px 20px" }}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className={`tag tag-${p.condition === 'new' ? 'brand' : 'success'}`}>{getConditionName(p.condition, t)}</span>
                <span className="text-sm text-muted" style={{ color: "var(--color-text-muted)" }}>{p.modelName} {p.brandName} {getPartTypeName(p.partType, t)}</span>
              </div>
              <div className="font-bold mb-1">{p.title}</div>
              <div className="text-sm text-muted mb-3" style={{ color: "var(--color-text-muted)" }}>{getLocalizedField(p, "cityName", lang)}</div>
              <div className={`car-card-price ${p.priceNegotiable === 1 ? "negotiable" : ""}`}>{p.priceNegotiable === 1 ? "NEGOTIABLE" : formatPrice(p.priceGnf)}</div>
              </div>
            </Link>))}
        </div>
      </div>
      </div>
    </div></>);
}